import { dayInZone } from '../../alerts/index.js';
import type { InventoryLevel } from '../interfaces/ILogistics.js';
/**
 * ISO instant → the calendar day (YYYY-MM-DD) it fell on **in the project's
 * timezone**.
 *
 * Slicing the instant instead takes the UTC day, which is a different date for
 * roughly a third of the clock in any given zone — a delivery stamped at 01:00
 * on a UTC+9 site would render as the previous day, and any day-count measured
 * against it inherits that off-by-one. The site works in its own zone, so the
 * conversion has to happen before the value reaches `formatDay`.
 *
 * The zone-aware YYYY-MM-DD formatting itself lives in `dayInZone`.
 */
export function zonedDay(iso: string, tz: string): string {
  return dayInZone(new Date(iso), tz);
}

/** `YYYY-MM-DD` plus `days` whole days. Parsed as UTC midnight so the arithmetic is a
 *  pure day count — the input is already a calendar day, so no zone applies. */
export function addDays(ymd: string, days: number): string {
  const date = new Date(`${ymd}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/** Whole days between two ISO instants (b − a), by calendar day in `tz`. */
export function daysBetween(aIso: string, bIso: string, tz: string): number {
  return Math.round((Date.parse(zonedDay(bIso, tz)) - Date.parse(zonedDay(aIso, tz))) / 86_400_000);
}

/**
 * Day-level date (YYYY-MM-DD) → compact localised label, e.g. "7/26" — or
 * "7/26/2026" with `withYear`, for a span that can cross years (a task's).
 *
 * Takes no timezone on purpose: a bare calendar day is already zone-free, and
 * the input here is always one (`dueDate`, `requestedDate`, or the output
 * of `zonedDay`, which did the conversion). Anchoring and re-formatting it in
 * some zone is a round trip that cannot change the answer — it only reads as if
 * a timezone decision were being made here. Convert instants with `zonedDay`
 * before calling this.
 */
export function formatDay(isoDate: string, lang: string, withYear = false): string {
  return new Intl.DateTimeFormat(lang === 'ja' ? 'ja-JP' : 'en-US', {
    year: withYear ? 'numeric' : undefined,
    month: 'numeric',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${isoDate}T00:00:00Z`));
}

/**
 * Whole days a bare `YYYY-MM-DD` deadline is past "today", in the project's
 * calendar. 0 = due today or not yet due. This is the low-level date primitive
 * (used by `etaLate` for an in-flight ETA); the delivery-aware, arrival-gated
 * count lives in `board.logic.ts`'s `overdueDays(delivery, …)`.
 *
 * `tz` decides which day "today" is: a site is already on tomorrow while UTC
 * is still on today, so a date reads past-due there first.
 */
export function daysPastDue(dueDate: string, now: Date, tz: string): number {
  const today = zonedDay(now.toISOString(), tz);
  const diff = (Date.parse(today) - Date.parse(dueDate)) / 86_400_000;
  return Math.max(0, Math.floor(diff));
}

/** Inventory-row tone (spec §3). needsCheck rows render no bar/badge at all;
 *  callers branch on that before asking for a tone.
 *
 *  There used to be a third tone, `stale`, for a row whose count was over 7 days
 *  old. It was removed: the threshold was a fixed 7 days regardless of span, so
 *  a material counted every 60 days spent ~87% of every cycle flagged — the
 *  label became the row's resting state rather than a signal. */
export type LevelTone = 'urgent' | 'warn' | 'ok';

/** Time-bar width % for an inventory level, clamped to [0, 100]. */
export function levelBarPct(level: InventoryLevel): number {
  return Math.min(100, Math.round((level.daysLeft / level.spanDays) * 100));
}

/** Solid-cell count for the 10-cell stock tank, in [0, 10].
 *  Clamped to ≥ 1 while any cover remains — a day of stock on a 60-day span
 *  must not render as an empty tank. */
export function levelCells(level: InventoryLevel): number {
  if (level.daysLeft <= 0) return 0;
  return Math.max(1, Math.round(levelBarPct(level) / 10));
}

/** Whole days the soonest in-flight arrival is past its own ETA, in the
 *  project's calendar. 0 when it is still ahead (or there is no ETA at all).
 *  Applies to an agreed date and a projection alike: neither is an arrival
 *  time once the day has gone by. */
export function etaLate(level: InventoryLevel, tz: string): number {
  return level.incomingEta ? daysPastDue(level.incomingEta, new Date(), tz) : 0;
}

/** Whole days a LANDED delivery arrived past its due date (the day it landed
 *  minus the due date, in the project's calendar). 0 when it was on time or
 *  early, or when there is no landed arrival / no due date to measure against.
 *  This is what lets a delivered row keep saying "arrived Nd overdue". */
export function arrivalLateDays(level: InventoryLevel, tz: string): number {
  if (!level.lastArrivalAt || !level.lastArrivalDueDate) return 0;
  const arrivedDay = zonedDay(level.lastArrivalAt, tz);
  return Math.max(
    0,
    Math.round((Date.parse(arrivedDay) - Date.parse(level.lastArrivalDueDate)) / 86_400_000),
  );
}

/**
 * The calendar day (YYYY-MM-DD, site tz) an order must go out to still land
 * before the material runs dry — the same boundary `overdue` reports as a
 * flag and `rowTone`/`ReorderTickTank` flip tone on (`daysLeft <=
 * leadTimeDays`, inclusive: that day is the last one an order still lands in
 * time). Extracted so the row's "Order by" line and the tank read the
 * identical day and can never drift apart. `null` when there is no lead time
 * to project a deadline from.
 */
export function orderByDate(level: InventoryLevel, tz: string): string | null {
  if (level.leadTimeDays == null) return null;
  const today = zonedDay(new Date().toISOString(), tz);
  return addDays(today, level.daysLeft - level.leadTimeDays);
}

/**
 * The tone the row's STOCK visuals wear — tank, badge and duration cap.
 *
 * An AMBER line takes the tank amber, even on a row with cover left: a delivery
 * past its ETA, or one that landed late, is a chase to make, and the gauge used
 * to stay green through it — an amber sentence under a green tank. (The older
 * rule was stock-decides-first, which is what put them out of step.) The amber
 * test therefore branches the way the render chain in `InventoryRow` does —
 * renewal before in-flight — so it reads the same line the row is showing.
 *
 * Past the tick with no amber line it is red — UNLESS help is on the way. An
 * on-time incoming delivery covers the gap, and its line is green, so the tank
 * is too. Nothing coming is the plain red.
 *
 * The tick day itself is amber, not red: its line says "order now" like the
 * days after it, but an order placed today still lands in time, so it is a
 * deadline rather than a shortfall. Red is what the row has left to say
 * tomorrow, when the same sentence means something worse.
 *
 * The one place tank and line deliberately diverge: a delivery that landed
 * ON TIME on a past-tick row draws the neutral "New delivery arrived · Sync"
 * line but keeps the tank red. That is not an oversight — the goods are on site
 * but uncounted, so the row still reads short until someone Syncs it, and a
 * green tank would claim a cover level nobody has confirmed.
 *
 * `needsCheck` rows never reach here — they render no bar or badge at all.
 */
export function rowTone(level: InventoryLevel, tz: string): LevelTone {
  const amberLine = level.awaitingRenewal
    ? arrivalLateDays(level, tz) > 0
    : level.deliveryIncoming && etaLate(level, tz) > 0;
  if (amberLine) return 'warn';
  if (!(level.overdue && level.leadTimeDays != null)) return 'ok';
  if (level.deliveryIncoming && !level.awaitingRenewal) return 'ok';
  return level.leadTimeDays > level.daysLeft ? 'urgent' : 'warn';
}
